/**
 * BB-14d1/BB-14d2c2 -- the browser's SIP and media leg.
 *
 * d1 built this and deliberately opened nothing. **d2c2 opens it**: the
 * WebSocket, the REGISTER, the INVITE into the agent's own conference,
 * and the two audio ends. What it still never does is decide whether
 * the agent is ready -- that answer comes from the backend's own
 * `GET /agent-media/readiness`, and this module only reports what it
 * observed so the hook can combine the two.
 *
 * **Why a class outside React.** A SIP session outlives any component:
 * a re-render, a route change, or a Strict Mode double-invoke must not
 * disturb a live media leg. React sees this only through
 * `useAgentMediaLeg`, which subscribes to state and issues intents.
 *
 * **Secret handling, unchanged from d1.** The SIP secret, the
 * `UserAgent`, the session, the microphone stream and the audio sink
 * all live in module-level `WeakMap`s rather than as instance fields,
 * so they are absent from `Object.keys`, `JSON.stringify`, spreads,
 * console dumps, and error serializers. A `MediaLegClient` can be
 * logged whole without leaking anything, and `getSnapshot()` is the
 * only thing that ever leaves. Nothing is written to `localStorage`,
 * `sessionStorage`, IndexedDB, a cookie, or a URL -- the secret exists
 * only for this tab's lifetime and dies with it.
 *
 * **Autoplay is not a media failure.** If the browser refuses to play
 * the remote stream without a gesture, the leg stays up and a bounded
 * `playbackBlocked` flag is raised. Tearing down a working SIP session
 * because an audio element would not autoplay would turn a one-click
 * annoyance into a lost campaign session.
 */

import {
  MediaLegEvent,
  MediaLegState,
  isSupersededBy,
  reduceMediaLegState,
} from './mediaLegStates';
import { ReadinessReason, requiresLocalTeardown } from './audioLifecycle';
import { releaseMicrophone } from './microphone';

/**
 * How long to wait for the registrar to accept a dispatched REGISTER.
 *
 * Well inside `CONNECTING_AUDIO_TIMEOUT_MS` (45s) so a registrar that
 * never answers is reported as a registration failure the agent can
 * retry, rather than as an anonymous product timeout.
 */
const REGISTRATION_TIMEOUT_MS = 10000;

/** Secrets, keyed by client. Never reachable from the instance. */
const secrets = new WeakMap();
/** Constructed UserAgents, keyed by client. Never exposed. */
const userAgents = new WeakMap();
/** Everything else with a lifetime: registerer, session, stream, sink. */
const resources = new WeakMap();

function resourcesFor(client) {
  let bag = resources.get(client);
  if (!bag) {
    bag = { sip: null, registerer: null, session: null, microphoneStream: null, audioSink: null };
    resources.set(client, bag);
  }
  return bag;
}

/**
 * Default SIP.js accessor. Imported lazily so that merely importing
 * this module -- which tests and non-media code do -- never pulls SIP.js
 * into a bundle path that could run transport code, and so the test
 * suite can exercise the whole lifecycle without the package present.
 */
async function defaultSipFactory() {
  const { UserAgent, Registerer, Inviter, SessionState } = await import('sip.js');
  return { UserAgent, Registerer, Inviter, SessionState };
}

/**
 * Translate a bootstrap payload into SIP.js `UserAgentOptions`.
 *
 * Exported because it is worth testing on its own: this is where the
 * transport URL, the AOR and the authorization credentials are decided,
 * and a mistake here would only surface as an opaque registration
 * failure.
 */
export function buildUserAgentOptions(bootstrap, sip) {
  if (!bootstrap || !bootstrap.issued) {
    throw new Error('bootstrap did not issue an identity');
  }
  const { peer_name: peerName, sip_realm: realm, media_wss_url: wssUrl } = bootstrap;
  if (!peerName || !realm || !wssUrl) {
    throw new Error('bootstrap is missing transport facts');
  }
  if (!sip || !sip.UserAgent || typeof sip.UserAgent.makeURI !== 'function') {
    throw new Error('sip module is missing makeURI');
  }

  // `sip:<peer>@<realm>` matches the realtime peer name Asterisk resolves
  // through the BB-14 authority.
  //
  // **This must be a real SIP.js `URI`, not a plain object.** SIP.js
  // merges `UserAgentOptions` over its own defaults, so a plain
  // `{user, host}` silently replaces the default `URI` instance and
  // `new UserAgent()` accepts it without complaint. The failure surfaces
  // one step later: `Registerer`'s constructor calls
  // `userAgent.configuration.uri.clone()`
  // (`sip.js/lib/api/registerer.js:34`), a plain object has no `clone`,
  // and it throws `TypeError` *after* the WebSocket is up and *before*
  // any REGISTER is sent. That is the exact Production failure of
  // 2026-09-08: WSS connected, no REGISTER, teardown, identity revoked.
  const uri = sip.UserAgent.makeURI(`sip:${peerName}@${realm}`);
  if (!uri) {
    // `makeURI` returns undefined rather than throwing on a malformed
    // URI. Failing here beats handing SIP.js an undefined it would only
    // replace with its own anonymous default.
    throw new Error('bootstrap transport facts do not form a valid SIP URI');
  }

  return {
    uri,
    authorizationUsername: peerName,
    authorizationPassword: bootstrap.sip_secret,
    transportOptions: {
      server: wssUrl,
      // Asterisk accepts the WebSocket subprotocol `sip` -- confirmed
      // from Production logs ("for protocol 'sip' accepted").
      protocols: ['sip'],
    },
    // The transport is started explicitly by `connect()`, never as a
    // side effect of construction: a constructed client that has not
    // been asked to connect must not hold an open socket.
    autoStart: false,
    // d2c2 supplies the microphone itself rather than letting SIP.js
    // prompt: acquisition has to happen before bootstrap, which is
    // earlier than any session exists.
    sessionDescriptionHandlerFactoryOptions: {
      constraints: { audio: true, video: false },
    },
  };
}

/**
 * The agent's conference target, derived from bootstrap alone.
 *
 * **Nothing here is hardcoded, and that is the point.** The dial prefix
 * and the conference extension are both connection-scoped facts the
 * backend reads from the provider connection and the agent's own live
 * session; a literal prefix or a literal extension baked in here would
 * silently send a second VICIdial deployment's agents into the first
 * one's conference.
 */
export function buildConferenceTarget(bootstrap) {
  const prefix = bootstrap && bootstrap.conference_dial_prefix;
  const exten = bootstrap && bootstrap.conf_exten;
  const realm = bootstrap && bootstrap.sip_realm;
  if (!prefix || !exten || !realm) {
    throw new Error('bootstrap is missing conference facts');
  }
  return `sip:${prefix}${exten}@${realm}`;
}

/**
 * Default audio sink. A real element is required -- a detached one is
 * enough for the browser to play into, and keeping it out of the React
 * tree means a re-render can never unmount live audio.
 */
function defaultAudioSinkFactory() {
  if (typeof document === 'undefined') {
    return null;
  }
  const element = document.createElement('audio');
  element.autoplay = true;
  element.playsInline = true;
  // Never controls, never in the layout: this is a speaker, not a
  // media player the agent operates.
  element.setAttribute('data-testid', 'agent-media-remote-audio');
  return element;
}

export class MediaLegClient {
  /**
   * @param {object} [options]
   * @param {(snapshot: object) => void} [options.onStateChange]
   * @param {() => Promise<object>} [options.sipFactory]
   *   Injected so tests exercise the full lifecycle without SIP.js.
   * @param {() => object} [options.audioSinkFactory]
   */
  constructor({
    onStateChange = null,
    sipFactory = defaultSipFactory,
    audioSinkFactory = defaultAudioSinkFactory,
  } = {}) {
    this._state = MediaLegState.DISCONNECTED;
    this._reason = null;
    this._peerName = null;
    this._expiresAt = null;
    this._leaseGeneration = null;
    this._sipRealm = null;
    this._mediaWssUrl = null;
    this._conferenceDialPrefix = null;
    this._confExten = null;
    this._conferenceTarget = null;
    this._sessionEstablished = false;
    this._playbackBlocked = false;
    this._onStateChange = onStateChange;
    this._sipFactory = sipFactory;
    this._audioSinkFactory = audioSinkFactory;
    this._disposed = false;
  }

  /** Non-secret snapshot. Safe to log, render, or put in telemetry. */
  getSnapshot() {
    return {
      state: this._state,
      reason: this._reason,
      peerName: this._peerName,
      expiresAt: this._expiresAt,
      leaseGeneration: this._leaseGeneration,
      sipRealm: this._sipRealm,
      mediaWssUrl: this._mediaWssUrl,
      conferenceDialPrefix: this._conferenceDialPrefix,
      confExten: this._confExten,
      conferenceTarget: this._conferenceTarget,
      sessionEstablished: this._sessionEstablished,
      playbackBlocked: this._playbackBlocked,
      hasUserAgent: userAgents.has(this),
    };
  }

  /** The exact peer this controller was issued. Used only as a revoke selector. */
  getPeerName() {
    return this._peerName;
  }

  /**
   * Construct the SIP client from a bootstrap payload.
   *
   * Leaves the leg in DISCONNECTED: preparing is not connecting.
   */
  async prepare(bootstrap) {
    // The SIP module is resolved first: `buildUserAgentOptions` needs
    // `UserAgent.makeURI` to build a real `URI`, and nothing may be
    // stored on this client until the options are known to be valid.
    const sip = await this._sipFactory();
    const options = buildUserAgentOptions(bootstrap, sip);

    secrets.set(this, bootstrap.sip_secret);
    this._peerName = bootstrap.peer_name;
    this._expiresAt = bootstrap.expires_at ?? null;
    this._leaseGeneration =
      typeof bootstrap.lease_generation === 'number' ? bootstrap.lease_generation : null;
    this._sipRealm = bootstrap.sip_realm;
    this._mediaWssUrl = bootstrap.media_wss_url;
    this._conferenceDialPrefix = bootstrap.conference_dial_prefix ?? null;
    this._confExten = bootstrap.conf_exten ?? null;

    resourcesFor(this).sip = sip;
    const userAgent = new sip.UserAgent(options);
    userAgents.set(this, userAgent);

    // Deliberately no transition: constructing is not connecting.
    this._emit();
    return this.getSnapshot();
  }

  /**
   * Open the transport and register.
   *
   * The microphone stream must already exist -- it is acquired before
   * bootstrap so that a denied microphone never consumes an identity --
   * and is held here so teardown can stop its tracks.
   */
  async connect({ microphoneStream = null } = {}) {
    const userAgent = userAgents.get(this);
    const bag = resourcesFor(this);
    if (!userAgent || !bag.sip) {
      throw new Error('connect() before prepare()');
    }
    bag.microphoneStream = microphoneStream;

    this._apply(MediaLegEvent.CONNECT_REQUESTED, 'connecting');
    try {
      await userAgent.start();
      const registerer = new bag.sip.Registerer(userAgent);
      bag.registerer = registerer;
      // `register()` resolves as soon as the REGISTER is *dispatched* --
      // `sip.js/lib/api/registerer.js` ends in
      // `return Promise.resolve(outgoingRegisterRequest)` -- so awaiting
      // it alone proves only that a request left the browser. Sending
      // the INVITE on that basis would race the registrar's own answer.
      // The registrar reaching `Registered` is the real precondition.
      const accepted = this._awaitRegistered(registerer, bag.sip);
      await registerer.register();
      await accepted;
    } catch (error) {
      // Only the shape of the failure is kept. A SIP.js error can carry
      // the full request, including the Authorization header.
      this._apply(MediaLegEvent.FAILURE, 'registration_failed');
      return this.getSnapshot();
    }
    this._apply(MediaLegEvent.REGISTERED, 'registered');
    return this.getSnapshot();
  }

  /**
   * INVITE the agent's own conference and wait for Established.
   *
   * Established is recorded, never published as readiness: it means the
   * browser's INVITE was answered, which says nothing about whether the
   * resulting channel appeared in the conference. `applyReadiness()`
   * supplies the half this cannot know.
   */
  async joinConference() {
    const userAgent = userAgents.get(this);
    const bag = resourcesFor(this);
    if (!userAgent || !bag.sip) {
      throw new Error('joinConference() before prepare()');
    }

    let target;
    try {
      target = buildConferenceTarget({
        conference_dial_prefix: this._conferenceDialPrefix,
        conf_exten: this._confExten,
        sip_realm: this._sipRealm,
      });
    } catch (error) {
      this._apply(MediaLegEvent.FAILURE, 'conference_not_configured');
      return this.getSnapshot();
    }
    this._conferenceTarget = target;

    this._apply(MediaLegEvent.JOIN_REQUESTED, 'joining');
    try {
      const uri = bag.sip.UserAgent.makeURI(target);
      if (!uri) {
        this._apply(MediaLegEvent.FAILURE, 'conference_not_configured');
        return this.getSnapshot();
      }
      const inviter = new bag.sip.Inviter(userAgent, uri, {
        sessionDescriptionHandlerOptions: {
          constraints: { audio: true, video: false },
        },
      });
      bag.session = inviter;

      const established = bag.sip.SessionState ? bag.sip.SessionState.Established : 'Established';
      const terminated = bag.sip.SessionState ? bag.sip.SessionState.Terminated : 'Terminated';
      inviter.stateChange.addListener((next) => {
        if (this._disposed) return;
        if (next === established) {
          this._sessionEstablished = true;
          this._attachRemoteAudio();
          this._emit();
          return;
        }
        if (next === terminated) {
          this._sessionEstablished = false;
          // Terminated after Established is media loss, not setup
          // failure: the leg existed and stopped. Either way the agent
          // must stop being told audio is connected.
          this._apply(MediaLegEvent.MEDIA_IMPAIRED, 'media_leg_ended');
        }
      });

      await inviter.invite();
    } catch (error) {
      this._apply(MediaLegEvent.FAILURE, 'invite_failed');
    }
    return this.getSnapshot();
  }

  /**
   * Fold one authoritative readiness answer into the leg.
   *
   * The backend is the only source of MEDIA_READY. `lease_superseded`
   * additionally tears this controller down: another tab holds the
   * session, and keeping a SIP leg alive against a superseded identity
   * is exactly the duplicate-audio condition the lease prevents.
   */
  applyReadiness(readiness) {
    const reason = (readiness && readiness.reason) || ReadinessReason.NOT_READY;

    if (requiresLocalTeardown(reason)) {
      this._apply(MediaLegEvent.SUPERSEDED, ReadinessReason.LEASE_SUPERSEDED);
      this._release();
      return this.getSnapshot();
    }
    if (readiness && readiness.ready === true) {
      // MEDIA_RECOVERED from DEGRADED, MEDIA_CONFIRMED from JOINING --
      // the reducer ignores whichever does not apply, so both are sent
      // rather than re-deriving the current state here.
      this._apply(MediaLegEvent.MEDIA_CONFIRMED, ReadinessReason.HEALTHY);
      this._apply(MediaLegEvent.MEDIA_RECOVERED, ReadinessReason.HEALTHY);
      return this.getSnapshot();
    }
    this._apply(MediaLegEvent.MEDIA_IMPAIRED, reason);
    return this.getSnapshot();
  }

  /**
   * Apply an observed lease generation. A strictly greater generation
   * means another tab won; this tab tears itself down rather than
   * waiting to lose a race server-side.
   */
  observeLeaseGeneration(observedGeneration) {
    if (!isSupersededBy(this._leaseGeneration, observedGeneration)) {
      return this.getSnapshot();
    }
    this._apply(MediaLegEvent.SUPERSEDED, 'superseded_by_newer_lease');
    this._release();
    return this.getSnapshot();
  }

  /**
   * Retry remote playback from a user gesture.
   *
   * Separate from every media path on purpose: a blocked autoplay is a
   * browser policy decision about *this* element, and resolving it must
   * not touch the SIP session.
   */
  async enablePlayback() {
    const played = await this._attemptPlayback();
    this._emit();
    return played;
  }

  /** The live audio sink, so a host can mount it if it chooses. */
  getAudioSink() {
    return resourcesFor(this).audioSink;
  }

  /** Operator/navigation teardown. Always safe, always idempotent. */
  teardown(reason = 'teardown') {
    this._apply(MediaLegEvent.TEARDOWN, reason);
    this._release();
    return this.getSnapshot();
  }

  /**
   * Feed a SIP-stack event through the state machine.
   */
  handleEvent(event, reason = null) {
    this._apply(event, reason);
    return this.getSnapshot();
  }

  /**
   * Resolve when the registrar accepts, reject when it refuses.
   *
   * Subscribed **before** `register()` is called, so a registrar that
   * answers inside the same task cannot land between the request going
   * out and the listener being attached. The listener is removed on
   * every exit path, including the timeout, so a late transition on a
   * torn-down registerer cannot settle a dead promise or leak.
   *
   * The timeout is a bound on a registrar that never answers at all --
   * not a race workaround. Without it a browser whose REGISTER is
   * black-holed would sit in Connecting Audio until the 45s product
   * timeout with nothing more specific to say.
   */
  _awaitRegistered(registerer, sip) {
    const states = (sip && sip.RegistererState) || {};
    const registered = states.Registered || 'Registered';
    const unregistered = states.Unregistered || 'Unregistered';
    const terminated = states.Terminated || 'Terminated';

    return new Promise((resolve, reject) => {
      let settled = false;
      let timer = null;

      const finish = (fn, value) => {
        if (settled) return;
        settled = true;
        if (timer) clearTimeout(timer);
        if (registerer.stateChange && typeof registerer.stateChange.removeListener === 'function') {
          registerer.stateChange.removeListener(listener);
        }
        fn(value);
      };

      function listener(next) {
        if (next === registered) {
          finish(resolve, undefined);
          return;
        }
        if (next === unregistered || next === terminated) {
          finish(reject, new Error('registration_refused'));
        }
      }

      if (!registerer.stateChange || typeof registerer.stateChange.addListener !== 'function') {
        // No observable state. Fall back to "the request was sent",
        // which is the pre-fix behaviour rather than a hard failure.
        resolve();
        return;
      }
      registerer.stateChange.addListener(listener);

      // Already accepted before we subscribed.
      if (registerer.state === registered) {
        finish(resolve, undefined);
        return;
      }

      timer = setTimeout(
        () => finish(reject, new Error('registration_timeout')),
        REGISTRATION_TIMEOUT_MS,
      );
    });
  }

  _attachRemoteAudio() {
    const bag = resourcesFor(this);
    const session = bag.session;
    if (!session) return;

    const handler = session.sessionDescriptionHandler;
    const peerConnection = handler && handler.peerConnection;
    if (!peerConnection || typeof peerConnection.getReceivers !== 'function') {
      return;
    }

    const remote =
      typeof MediaStream === 'function' ? new MediaStream() : { addTrack() {}, getTracks: () => [] };
    for (const receiver of peerConnection.getReceivers()) {
      if (receiver && receiver.track) {
        remote.addTrack(receiver.track);
      }
    }

    if (!bag.audioSink) {
      bag.audioSink = this._audioSinkFactory();
    }
    if (!bag.audioSink) return;
    bag.audioSink.srcObject = remote;
    // Fire and forget: the promise is handled inside, and awaiting it
    // here would make attachment depend on autoplay policy.
    this._attemptPlayback().then(() => this._emit());
  }

  async _attemptPlayback() {
    const bag = resourcesFor(this);
    const sink = bag.audioSink;
    if (!sink || typeof sink.play !== 'function') {
      return false;
    }
    try {
      await sink.play();
      this._playbackBlocked = false;
      return true;
    } catch (error) {
      // Autoplay refused. The leg is untouched -- this is a gesture
      // requirement, not a media failure.
      this._playbackBlocked = true;
      return false;
    }
  }

  _apply(event, reason) {
    const result = reduceMediaLegState(this._state, event, reason);
    const changed = result.changed || result.reason !== this._reason;
    this._state = result.state;
    this._reason = result.reason;
    if (changed) {
      this._emit();
    }
  }

  /**
   * Drop every held resource.
   *
   * `WeakMap.delete` removes the only reference this module holds to
   * the secret and the UserAgent, so both become collectable
   * immediately. The microphone is stopped explicitly: leaving its
   * tracks live keeps the browser's recording indicator lit after the
   * agent has left the campaign, which reads as the product still
   * listening.
   */
  _release() {
    const wasEstablished = this._sessionEstablished;
    this._disposed = true;
    this._sessionEstablished = false;
    this._playbackBlocked = false;
    const bag = resources.get(this);
    if (bag) {
      if (bag.session) {
        // BYE ends an established dialog; CANCEL withdraws an INVITE
        // that never got one. Sending the wrong one is rejected by
        // SIP.js, so the phase decides -- and either way an error here
        // must not stop the rest of teardown.
        const end = wasEstablished ? bag.session.bye : bag.session.cancel;
        if (typeof end === 'function') {
          try {
            const result = end.call(bag.session);
            if (result && typeof result.catch === 'function') {
              result.catch(() => {});
            }
          } catch (error) {
            /* teardown is best-effort */
          }
        }
      }
      if (bag.registerer && typeof bag.registerer.unregister === 'function') {
        try {
          const result = bag.registerer.unregister();
          if (result && typeof result.catch === 'function') {
            result.catch(() => {});
          }
        } catch (error) {
          /* best-effort */
        }
      }
      releaseMicrophone(bag.microphoneStream);
      if (bag.audioSink) {
        bag.audioSink.srcObject = null;
        if (typeof bag.audioSink.pause === 'function') {
          try {
            bag.audioSink.pause();
          } catch (error) {
            /* best-effort */
          }
        }
      }
      bag.session = null;
      bag.registerer = null;
      bag.microphoneStream = null;
      bag.audioSink = null;
    }
    const userAgent = userAgents.get(this);
    if (userAgent && typeof userAgent.stop === 'function') {
      try {
        userAgent.stop();
      } catch (error) {
        /* best-effort */
      }
    }
    secrets.delete(this);
    userAgents.delete(this);
  }

  _emit() {
    if (typeof this._onStateChange === 'function') {
      this._onStateChange(this.getSnapshot());
    }
  }
}

/** Test seam only: proves the secret is reachable nowhere else. */
export function __secretIsHeldFor(client) {
  return secrets.has(client);
}
